import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, PartyPopper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { useParams, Navigate } from 'react-router-dom';
import ConversionSettings from './components/ConversionSettings';
import UploadedImagesTable from './components/UploadedImagesTable';
import ConvertedImagesTable from './components/ConvertedImagesTable';
import ImagePreview from './components/ImagePreview';
import FormatComparison from './components/FormatComparison';
import { getConversionBySlug } from './data/conversions';
import {
  ImageFormat,
  ScaleOption,
  UploadedImage,
  ConvertedImage,
  convertImage,
  downloadImage,
  downloadAllImages,
  estimateOutputSize,
  formatBytes
} from './services/imageService';

function ConversionPage() {
  const { t, i18n } = useTranslation(['translation', 'convert']);
  const { lang, slug } = useParams<{ lang: string; slug: string }>();
  
  const conversion = getConversionBySlug(slug || '');
  
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [convertedImages, setConvertedImages] = useState<ConvertedImage[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ImageFormat>(conversion?.to || 'webp');
  const [compressionLevel, setCompressionLevel] = useState<number>(80);
  const [scaleOption, setScaleOption] = useState<ScaleOption>(1);

  const [conversionProgress, setConversionProgress] = useState<Record<string, number>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [predictedSize, setPredictedSize] = useState<string | null>(null);
  const [showUploadArea, setShowUploadArea] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMoreInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  if (!conversion) {
    return <Navigate to={`/${lang || 'en'}/404`} replace />;
  }

  // Update selected format when conversion changes
  useEffect(() => {
    if (conversion) {
      setSelectedFormat(conversion.to);
    }
  }, [conversion]);

  useEffect(() => {
    return () => {
      uploadedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
      convertedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    if (uploadedImages.length > 0) {
      updatePredictedSize();
    }
  }, [selectedFormat, compressionLevel, uploadedImages.length]);

  useEffect(() => {
    if (uploadedImages.length > 0) {
      setShowUploadArea(false);
    }
  }, [uploadedImages.length]);

  useEffect(() => {
    if (convertedImages.length === 0 && uploadedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [convertedImages.length, uploadedImages.length]);

  useEffect(() => {
    const handleProgress = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.fileName && typeof detail?.progress === 'number') {
        setConversionProgress(prev => ({ ...prev, [detail.fileName]: detail.progress }));
      }
    };

    window.addEventListener('conversionProgress', handleProgress);
    return () => window.removeEventListener('conversionProgress', handleProgress);
  }, []);

  const updatePredictedSize = useCallback(() => {
    if (uploadedImages.length === 0) {
      setPredictedSize(null);
      return;
    }

    const totalOriginalSize = uploadedImages.reduce((sum, img) => sum + img.size, 0);
    const estimatedSize = estimateOutputSize(totalOriginalSize, selectedFormat, compressionLevel);
    setPredictedSize(formatBytes(estimatedSize));
  }, [uploadedImages, selectedFormat, compressionLevel]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      toast.error('Please select valid image files');
      return;
    }
    
    const oversizedFiles = imageFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed the 10MB size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    const newUploadedImages = imageFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      size: file.size,
      previewUrl: URL.createObjectURL(file)
    }));
    
    setUploadedImages(prev => [...prev, ...newUploadedImages]);
    setShowUploadArea(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (uploadMoreInputRef.current) {
      uploadMoreInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        handleFiles(imageFiles);
        toast.success(`${imageFiles.length} image(s) pasted from clipboard`);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFiles]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    handleFiles(e.target.files);
  }, [handleFiles]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleConvertImages = useCallback(async () => {
    if (uploadedImages.length === 0) return;
    
    setIsConverting(true);
    let newConvertedImages: ConvertedImage[] = [];

    try {
      setConversionProgress({});
      const convertedImagesPromises = uploadedImages.map(img => 
        convertImage(img.file, selectedFormat, compressionLevel, scaleOption));
      const results = await Promise.allSettled(convertedImagesPromises);
      
      newConvertedImages = results
        .filter((result): result is PromiseFulfilledResult<ConvertedImage> => result.status === 'fulfilled')
        .map(result => result.value);
      
      const failedResults = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map(result => (result as PromiseRejectedResult).reason);
      
      if (failedResults.length > 0) {
        console.error('Some images failed to convert:', failedResults);
        toast.error(`${failedResults.length} image(s) failed to convert. The rest were processed successfully.`);
      }
      
      uploadedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
      
      setConvertedImages(prev => [...prev, ...newConvertedImages]);
      setUploadedImages([]);
      setPredictedSize(null);
      setShowUploadArea(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to convert images';
      console.error('Error converting images:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsConverting(false);
      setConversionProgress({});
    }
  }, [uploadedImages, selectedFormat, compressionLevel, scaleOption]);

  const handleShowUploadArea = useCallback(() => {
    if (uploadMoreInputRef.current) {
      uploadMoreInputRef.current.click();
    }
  }, []);

  const handleRemoveUploadedImage = (id: string) => {
    setUploadedImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      const removed = prev.find(img => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      
      if (filtered.length === 0 && convertedImages.length === 0) {
        setShowUploadArea(true);
      }
      
      return filtered;
    });
  };

  const handleRemoveConvertedImage = (id: string) => {
    setConvertedImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      const removed = prev.find(img => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      
      if (filtered.length === 0 && uploadedImages.length === 0) {
        setShowUploadArea(true);
      }
      
      return filtered;
    });
  };

  const handleClearUploaded = useCallback(() => {
    uploadedImages.forEach(img => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setUploadedImages([]);
    setPredictedSize(null);
    if (convertedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [convertedImages.length]);

  const handleClearConverted = useCallback(() => {
    convertedImages.forEach(img => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setConvertedImages([]);
    if (uploadedImages.length === 0 && convertedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [uploadedImages.length]);

  const handleDownloadImage = useCallback(async (image: ConvertedImage) => {
    if (!image?.convertedBlob) {
      throw new Error('No converted data available for download');
    }
    
    try {
      await downloadImage(image);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download image';
      console.error('Error downloading image:', errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (convertedImages.length === 0) return;
    
    try {
      await downloadAllImages(convertedImages);
    } catch (error) {
      console.error('Error downloading all images:', error);
    }
  }, [convertedImages]);

  const formatDisplayNames: Record<ImageFormat, string> = {
    jpeg: 'JPEG',
    png: 'PNG',
    webp: 'WebP',
    avif: 'AVIF',
    gif: 'GIF',
    bmp: 'BMP',
    ico: 'ICO',
    svg: 'SVG'
  };

  const pageTitle = t('convert:title', { 
    from: formatDisplayNames[conversion.from], 
    to: formatDisplayNames[conversion.to] 
  });
  const pageDescription = t('convert:description', { 
    from: formatDisplayNames[conversion.from], 
    to: formatDisplayNames[conversion.to] 
  });

  const canonicalUrl = `https://shiftpic.com/${lang || i18n.language.split('-')[0]}/convert/${slug}`;

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": pageTitle,
    "description": pageDescription,
    "url": canonicalUrl,
    "applicationCategory": "ImageConverter",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      `Convert ${formatDisplayNames[conversion.from]} to ${formatDisplayNames[conversion.to]}`,
      "Client-side processing for privacy",
      "Batch conversion support",
      "Quality control",
      "Image scaling"
    ]
  };

  const acceptFormat = conversion.from === 'svg' ? 'image/svg+xml' : 
                       conversion.from === 'jpeg' ? 'image/jpeg' :
                       `image/${conversion.from}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle} - ShiftPic</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`${pageTitle} - ShiftPic`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:title" content={`${pageTitle} - ShiftPic`} />
        <meta name="twitter:description" content={pageDescription} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdSchema)}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-[800px] mx-auto py-8 text-center">
          <h1 className="flex items-center justify-center gap-3">
            {convertedImages.length > 0 ? (
              <>
                <PartyPopper className="text-accent" size={32} aria-hidden="true" />
                {t('convert:converted', { 
                  from: formatDisplayNames[conversion.from], 
                  to: formatDisplayNames[conversion.to] 
                })}
              </>
            ) : (
              pageTitle
            )}
          </h1>
          {uploadedImages.length === 0 && convertedImages.length === 0 && (
            <p>{pageDescription}</p>
          )}
        </div>
        
        <input 
          type="file" 
          ref={uploadMoreInputRef}
          multiple 
          accept={acceptFormat}
          onChange={handleFileChange} 
          className="hidden" 
          aria-label="Upload more images"
        />
        
        {showUploadArea && (
          <section className="mb-8" aria-labelledby="upload-section">
            <div className="upload-card-container">
              <div className="upload-card-body">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div className="flex-1">
                    <div 
                      ref={dropZoneRef}
                      className={`upload-card-dropzone group ${isDragging ? 'dragging' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                      onDragEnter={handleDragEnter}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          fileInputRef.current?.click();
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-labelledby="upload-heading"
                    >
                      <input 
                        type="file" 
                        ref={fileInputRef}
                        multiple 
                        accept={acceptFormat}
                        onChange={handleFileChange} 
                        className="hidden" 
                        aria-label="File upload"
                      />
                      <Upload
                        className="upload-card-icon"
                        size={48} 
                        aria-hidden="true" 
                      />
                      <h2 id="upload-heading" className="upload-card-title">
                        {t('convert:uploadPrompt', { 
                          format: formatDisplayNames[conversion.from] 
                        })}
                      </h2>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {uploadedImages.length > 0 && (
          <ConversionSettings
            selectedFormat={selectedFormat}
            setSelectedFormat={setSelectedFormat}
            compressionLevel={compressionLevel}
            setCompressionLevel={setCompressionLevel}
            scaleOption={scaleOption}
            setScaleOption={setScaleOption}
            predictedSize={predictedSize}
            uploadedImages={uploadedImages}
          />
        )}

        {uploadedImages.length > 0 && (
          <UploadedImagesTable
            images={uploadedImages}
            onRemove={handleRemoveUploadedImage}
            onPreview={setPreviewImage}
            onUploadMore={handleShowUploadArea} 
            onConvert={handleConvertImages} 
            onClear={handleClearUploaded} 
            isConverting={isConverting} 
            selectedFormat={selectedFormat} 
            compressionLevel={compressionLevel}
            conversionProgress={conversionProgress}
          />
        )}

        <ConvertedImagesTable
          images={convertedImages}
          onDownload={handleDownloadImage}
          onDownloadAll={handleDownloadAll}
          onRemove={handleRemoveConvertedImage}
          onClear={handleClearConverted}
          onPreview={setPreviewImage}
        />

        <ImagePreview
          previewImage={previewImage}
          onClose={(e) => {
            e.stopPropagation();
            setPreviewImage(null);
          }}
          onImageClick={(e) => e.stopPropagation()}
        />

        {showUploadArea && (
          <FormatComparison 
            from={conversion.from} 
            to={conversion.to} 
            slug={slug || ''} 
          />
        )}
      </div>
    </>
  );
}

export default ConversionPage;
